import { Combobox, type Option } from '@/components/ui/combobox/combobox';
import KeyboardKeyIcon from '@/components/ui/keyboard-key-icon';
import { HOTKEYS } from '@/lib/hotkeys';
import { pdkAxios } from '@/lib/pdk-axios';
import type { Deal } from '@/models/registry';
import { dealStore } from '@/models/stores/deals';
import { type DeliveryStage } from '@/models/types.ts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  coerceDeliveryStage,
  getAllowedDeliveryStages,
} from './delivery-stage-logic';
import { DELIVERY_STAGE_OPTIONS } from './delivery-stage-options';

type DeliveryStageCombobox = {
  id: Deal['id'];
  shortId: Deal['short_id'];
  dealStatus: Deal['status'];
  value?: DeliveryStage;
  afterOnChange?: () => void;
  onSuccess?: (data: Deal, variables: { deliveryStage: DeliveryStage }) => void;
  onError?: (error: Error, variables: { deliveryStage: DeliveryStage }) => void;
};

export function DeliveryStageCombobox({
  id,
  shortId,
  dealStatus,
  value,
  afterOnChange,
  onError,
  onSuccess,
}: DeliveryStageCombobox) {
  const queryClient = useQueryClient();

  const lockedDeliveryStage = dealStatus !== 'awaiting_delivery';
  const allowedDeliveryStages = useMemo(
    () => getAllowedDeliveryStages(dealStatus),
    [dealStatus],
  );

  const coercedDeliveryStage = coerceDeliveryStage(dealStatus, value);

  const filteredOptions: Option<DeliveryStage>[] = useMemo(
    () =>
      DELIVERY_STAGE_OPTIONS.filter((option) =>
        allowedDeliveryStages.includes(option.value),
      ),
    [allowedDeliveryStages],
  );

  const currentOption = useMemo(() => {
    return filteredOptions.find(
      (option) => option.value === coercedDeliveryStage,
    );
  }, [filteredOptions, coercedDeliveryStage]);

  const updateDeliveryStageMutation = useMutation({
    onMutate: async ({ deliveryStage }) => {
      dealStore.updateDeliveryStage({ id, deliveryStage });
    },
    mutationFn: async ({ deliveryStage }: { deliveryStage: DeliveryStage }) => {
      const response = await pdkAxios
        .post(`/deals/${id}/delivery-stage`, { delivery_stage: deliveryStage })
        .catch((e) => {
          console.error('Error updating delivery stage', e);
          throw e;
        });

      return response.data;
    },
    onSuccess: (data, variables) => {
      onSuccess?.(data, variables);
    },
    onError: (error, variables) => {
      onError?.(error, variables);

      console.error('Error updating delivery stage', error, variables);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['deal', shortId] });
    },
  });

  const handleDeliveryStageChange = (newDeliveryStage: DeliveryStage) => {
    if (lockedDeliveryStage) return;
    if (!allowedDeliveryStages.includes(newDeliveryStage)) return;
    updateDeliveryStageMutation.mutate({
      deliveryStage: newDeliveryStage,
    });
    afterOnChange?.();
  };

  return (
    <Combobox
      endAdornment={
        <KeyboardKeyIcon>{HOTKEYS.DEAL_DELIVERY_STAGE}</KeyboardKeyIcon>
      }
      placeholder="Set delivery stage..."
      emptyMessage="No delivery stage found"
      currentOption={currentOption}
      options={filteredOptions}
      onKeyDown={(e) => {
        const keyPressed = e.key;

        const option = DELIVERY_STAGE_OPTIONS.find(
          (option) => option.hotkey === keyPressed,
        );

        if (option) {
          handleDeliveryStageChange(option.value);
          e.preventDefault();
          e.stopPropagation();
        }
      }}
      onChange={handleDeliveryStageChange}
      setOpen={() => {}}
    ></Combobox>
  );
}
